import { FC, memo, useEffect, useMemo, useState } from 'react';
import * as am5 from '@amcharts/amcharts5';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5percent from '@amcharts/amcharts5/percent';
import { Card, Empty, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { ViewCount, fetchViewCount } from 'services/api/statistics';

import { Loading } from 'components/ui';

const { Title } = Typography;

const ITEM_HEIGHT = 250;

const PageView: FC = () => {
  const { t } = useTranslation();

  const [viewCount, setViewCount] = useState<ViewCount | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchViewCount()
      .then(setViewCount)
      .catch(() => setViewCount(null))
      .finally(() => setLoading(false));
  }, []);

  const viewByCountry = useMemo(
    () => viewCount?.view_by_country || {},
    [viewCount?.view_by_country]
  );

  useEffect(() => {
    if (!Object.keys(viewByCountry).length) {
      return;
    }

    const root = am5.Root.new('PageView');

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5percent.PieChart.new(root, {
        layout: root.verticalLayout,
        innerRadius: am5.percent(50)
      })
    );

    const series = chart.series.push(
      am5percent.PieSeries.new(root, {
        valueField: 'value',
        categoryField: 'category',
        alignLabels: false
      })
    );

    series.labels.template.setAll({
      text: '{value}',
      forceHidden: false
    });

    series.data.setAll(
      Object.keys(viewByCountry).map(key => ({
        category: key,
        value: viewByCountry[key] || 0
      }))
    );

    series.appear(1000, 100);

    return () => root.dispose();
  }, [viewByCountry]);

  return (
    <Card
      title={
        <>
          <Title level={5} style={{ marginTop: 16 }}>
            {t('dashboard.page_views')}
          </Title>
          <Title level={2} style={{ marginTop: 4 }}>
            {t('dashboard.total', {
              count: viewCount?.total || 0
            })}
          </Title>
        </>
      }
      className="chart-card"
    >
      {Object.keys(viewByCountry).length ? (
        <div id="PageView" style={{ height: ITEM_HEIGHT }} />
      ) : (
        <Empty style={{ height: ITEM_HEIGHT }} />
      )}
      {loading && <Loading type="overlay" blurred />}
    </Card>
  );
};

export default memo(PageView);
